import { useMemo } from 'react';
import { Flex, Typography } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { TableRowActionsMenu } from '~/app/components/common/TableRowActionsMenu/TableRowActionsMenu';
import { useTranslation } from 'react-i18next';
import type { LandlordPartnerRowDto } from '@evimo/client/models';
import { SlaStatusTag } from '~/app/landing/sla/shared/components/SlaStatusTag';
import { SlaPartnerKindDot } from '~/app/landing/sla/shared/components/SlaPartnerKindDot';
import { formatCurrency } from '~/app/landing/sla/shared/utils/sla-format.utils';

type Args = {
  onEdit: (row: LandlordPartnerRowDto) => void;
  canManage: boolean;
};

export function useLandlordColumns({ onEdit, canManage }: Args) {
  const { t } = useTranslation('sla');

  return useMemo(
    () => [
      {
        title: t('landlords.columns.partner'),
        dataIndex: 'partnerName',
        key: 'partnerName',
        render: (name: string) => (
          <Flex align="center" gap={10}>
            <SlaPartnerKindDot variant="landlord" />
            <Typography.Text strong>{name}</Typography.Text>
          </Flex>
        ),
      },
      {
        title: t('landlords.columns.uptime'),
        key: 'uptime',
        width: 120,
        render: (_: unknown, row: LandlordPartnerRowDto) =>
          row.slaContract?.uptimeTargetPct != null
            ? `${row.slaContract.uptimeTargetPct}%`
            : '—',
      },
      {
        title: t('landlords.columns.response'),
        key: 'response',
        width: 140,
        render: (_: unknown, row: LandlordPartnerRowDto) =>
          row.slaContract
            ? `${row.slaContract.responseDcDays}d / ${row.slaContract.responseAcDays}d`
            : '—',
      },
      {
        title: t('landlords.columns.penalty'),
        key: 'penalty',
        width: 130,
        render: (_: unknown, row: LandlordPartnerRowDto) =>
          row.slaContract
            ? `${formatCurrency(
                row.slaContract.penaltyPerDayAmount,
                row.slaContract.penaltyCurrency,
              )}/d`
            : '—',
      },
      {
        title: t('landlords.columns.status'),
        key: 'status',
        width: 110,
        render: (_: unknown, row: LandlordPartnerRowDto) =>
          row.slaContract ? (
            <SlaStatusTag status={row.slaContract.status} />
          ) : (
            '—'
          ),
      },
      {
        title: t('landlords.columns.sameCompany'),
        key: 'sameCompany',
        width: 120,
        render: (_: unknown, row: LandlordPartnerRowDto) =>
          row.isSameCompanyAsOperator ? (
            <Typography.Text type="secondary">
              {t('landlords.sameCompany')}
            </Typography.Text>
          ) : null,
      },
      ...(canManage
        ? [
            {
              key: 'actions',
              width: 56,
              align: 'center' as const,
              render: (_: unknown, row: LandlordPartnerRowDto) => (
                <TableRowActionsMenu
                  items={[
                    {
                      key: 'edit',
                      label: t('actions.edit'),
                      icon: <EditOutlined />,
                      onClick: () => onEdit(row),
                    },
                  ]}
                />
              ),
            },
          ]
        : []),
    ],
    [canManage, onEdit, t],
  );
}
